import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Spinner, Button, Tabs, Tab, Form, Modal } from 'react-bootstrap';
import { useApi } from '../../contexts/ApiProvider';
import { useParams, useNavigate } from 'react-router-dom';

// Import the new sub-components
import TripLogistics from './tabs/TripLogistics';
import TripPackingMatrix from './tabs/TripPackingMatrix';
import TripLedger from './tabs/TripLedger';

export default function TripDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const api = useApi();

    const [trip, setTrip] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('packing');

    // Edit Trip State (Parent keeps this since it affects the Header)
    const [showEditTrip, setShowEditTrip] = useState(false);
    const [editTripData, setEditTripData] = useState({ location: '', start_date: '', end_date: '' });

    useEffect(() => {
        fetchTripDetails();
        // eslint-disable-next-line
    }, [id]);

    const fetchTripDetails = async () => {
        setIsLoading(true);
        const res = await api.get(`/travel/trips/${id}`);
        if (res.ok) setTrip(res.body);
        setIsLoading(false);
    };

    const handleStatusChange = async (newStatus) => {
        setTrip(prev => ({ ...prev, status: newStatus }));
        const res = await api.put(`/travel/trips/${id}`, { status: newStatus });
        if (!res.ok) fetchTripDetails();
    };

    const handleUpdatePhotoAlbum = async () => {
        const newUrl = window.prompt("Enter the link to your photo album:", trip.photo_album_url || "");
        if (newUrl !== null) {
            setTrip(prev => ({ ...prev, photo_album_url: newUrl }));
            const res = await api.put(`/travel/trips/${id}`, { photo_album_url: newUrl });
            if (!res.ok) fetchTripDetails();
        }
    };

    const handleUpdateTrip = async (e) => {
        e.preventDefault();
        const res = await api.put(`/travel/trips/${id}`, editTripData);
        if (res.ok) {
            setShowEditTrip(false);
            fetchTripDetails();
        }
    };

    if (isLoading && !trip) return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;
    if (!trip) return <div className="text-center py-5 text-muted">Trip not found.</div>;

    return (
        <Container className="py-4 fade-in">
            <Button variant="link" className="text-muted p-0 mb-3 text-decoration-none" onClick={() => navigate('/travel')}>
                <i className="bi bi-arrow-left me-2"></i>Back to Command Center
            </Button>

            <div className="d-flex justify-content-between align-items-start mb-4">
                <div>
                    <div className="d-flex align-items-center gap-3 mb-1">
                        <h2 className="fw-bold mb-0 me-2">{trip.location}</h2>
                        <Button variant="link" className="text-muted p-0 me-3" onClick={() => {
                            setEditTripData({
                                location: trip.location,
                                start_date: new Date(trip.start_date).toISOString().slice(0, 10),
                                end_date: new Date(trip.end_date).toISOString().slice(0, 10)
                            });
                            setShowEditTrip(true);
                        }}>
                            <i className="bi bi-pencil-square"></i>
                        </Button>

                        {trip.budget_bucket_id && (
                            <Badge bg="success" className="bg-opacity-10 text-success border border-success rounded-pill px-3 py-2">
                                <i className="bi bi-safe2-fill me-1"></i> Budget Linked
                            </Badge>
                        )}

                        {trip.photo_album_url ? (
                            <div className="d-flex align-items-center">
                                <Button variant="outline-primary" size="sm" className="rounded-pill fw-bold" onClick={() => window.open(trip.photo_album_url, '_blank')}>
                                    <i className="bi bi-images me-2"></i>View Album
                                </Button>
                                <Button variant="link" size="sm" className="text-muted ms-1 p-0" onClick={handleUpdatePhotoAlbum}>
                                    <i className="bi bi-pencil-square"></i>
                                </Button>
                            </div>
                        ) : (
                            <Button variant="light" size="sm" className="rounded-pill fw-bold text-muted border" onClick={handleUpdatePhotoAlbum}>
                                <i className="bi bi-plus-circle me-2"></i>Add Photos Link
                            </Button>
                        )}
                    </div>
                    <p className="text-muted fw-bold mb-0">
                        <i className="bi bi-calendar3 me-2"></i>
                        {new Date(trip.start_date).toLocaleDateString()} - {new Date(trip.end_date).toLocaleDateString()}
                        {trip.group && <><span className="mx-3">•</span><i className="bi bi-people-fill me-2"></i>{trip.group.name}</>}
                    </p>
                </div>

                <div style={{ width: '150px' }}>
                    <Form.Select size="sm" value={trip.status} onChange={(e) => handleStatusChange(e.target.value)}
                        className={`fw-bold text-${trip.status === 'Completed' ? 'secondary' : 'primary'} border-${trip.status === 'Completed' ? 'secondary' : 'primary'}`}>
                        <option value="Planning">Planning</option>
                        <option value="Active">Active</option>
                        <option value="Completed">Completed</option>
                        <option value="Cancelled">Cancelled</option>
                    </Form.Select>
                </div>
            </div>

            <Card className="border-0 shadow-sm rounded-4 overflow-hidden mt-4">
                <Card.Body className="p-0">
                    <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="px-4 pt-3 border-bottom custom-tabs">
                        <Tab eventKey="overview" title={<><i className="bi bi-map me-2"></i>Logistics</>}>
                            <TripLogistics tripId={trip.id} />
                        </Tab>
                        <Tab eventKey="packing" title={<><i className="bi bi-luggage me-2"></i>Packing Matrix</>}>
                            <TripPackingMatrix trip={trip} />
                        </Tab>
                        <Tab eventKey="budget" title={<><i className="bi bi-currency-dollar me-2"></i>Ledger</>}>
                            <TripLedger tripId={trip.id} onBudgetInitialized={fetchTripDetails} />
                        </Tab>
                    </Tabs>
                </Card.Body>
            </Card>

            <Modal show={showEditTrip} onHide={() => setShowEditTrip(false)} centered>
                <Modal.Header closeButton className="border-0 pb-0"><Modal.Title className="fw-bold">Edit Trip Details</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleUpdateTrip}>
                        <Form.Group className="mb-3">
                            <Form.Label className="small fw-bold text-muted mb-1">Location / Name</Form.Label>
                            <Form.Control type="text" value={editTripData.location} onChange={e => setEditTripData({...editTripData, location: e.target.value})} required />
                        </Form.Group>
                        <Row className="mb-4 g-2">
                            <Col><Form.Label className="small fw-bold text-muted mb-1">Start Date</Form.Label><Form.Control type="date" value={editTripData.start_date} onChange={e => setEditTripData({...editTripData, start_date: e.target.value})} required /></Col>
                            <Col><Form.Label className="small fw-bold text-muted mb-1">End Date</Form.Label><Form.Control type="date" value={editTripData.end_date} onChange={e => setEditTripData({...editTripData, end_date: e.target.value})} required /></Col>
                        </Row>
                        <Button variant="primary" type="submit" className="w-100 fw-bold rounded-pill">Save Changes</Button>
                    </Form>
                </Modal.Body>
            </Modal>
        </Container>
    );
}
